import { Icd10Diagnosis } from '@lib/dictionaries/core/models/icd10-diagnosis.interface';

/** The part of an МКХ-10 dictionary entry the tree and its expansion read. */
export type Icd10DictionaryEntry = Pick<Icd10Diagnosis, 'code' | 'level'> & Partial<Pick<Icd10Diagnosis, 'nameUa' | 'nameEn'>>;

/** What a diagnoses tree offers on each row: removing it from a list, or adding it from the dictionary. */
export type Icd10TreeAction = 'remove' | 'add';

/** ICD-10 dictionary level of a block of categories, e.g. `O94-O99`. */
export const ICD10_BLOCK_LEVEL = 2;
/** ICD-10 dictionary level of a category, e.g. `O98`. */
export const ICD10_CATEGORY_LEVEL = 3;
/** Characters a dictionary search needs before it is asked. */
export const ICD10_SEARCH_MIN_LENGTH = 2;

export interface Icd10CategoryGroup {
  category: string;
  codes: string[];
}

export interface Icd10BlockGroup {
  /** Block code (`O94-O99`), or the code's letter when no block covers its category. */
  key: string;
  count: number;
  categories: Icd10CategoryGroup[];
}

/** Loading state of a names lookup for one tree node (block, category, or the block list itself). */
export interface Icd10NamesLoadState {
  loading: boolean;
  error: boolean;
}

interface Icd10BlockRange {
  code: string;
  start: string;
  end: string;
}

export function compareIcd10Codes(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true });
}

/** `O98.4` → `O98`. */
export function icd10Category(code: string): string {
  return code.split('.')[0];
}

export function icd10DisplayName(entry: { nameUa?: string | null; nameEn?: string | null }): string {
  return entry.nameUa || entry.nameEn || '';
}

/**
 * Letter + first digit of each category (`O98` → `O9`). Dictionary search matches a substring of both code and
 * names, so these short prefixes find a block's categories without dragging in every name that contains a letter.
 */
export function icd10CategoryPrefixes(categories: string[]): string[] {
  return [...new Set(categories.map(category => category.slice(0, 2)))];
}

/**
 * Groups codes into block → category → codes. A category goes under the block whose `start-end` range contains it;
 * categories no block covers are grouped by their letter. Blocks, categories and codes are sorted numerically.
 */
export function groupIcd10Codes(codes: string[], blockCodes: string[]): Icd10BlockGroup[] {
  const blocks = toBlockRanges(blockCodes);
  const categoryCodes = new Map<string, Set<string>>();

  for (const raw of codes) {
    const code = raw.trim().toUpperCase();
    if (!code) continue;

    const category = icd10Category(code);
    categoryCodes.set(category, (categoryCodes.get(category) ?? new Set<string>()).add(code));
  }

  const groups = new Map<string, Map<string, Set<string>>>();
  for (const [category, codesInCategory] of categoryCodes) {
    const key = blocks.find(block => block.start <= category && category <= block.end)?.code ?? category[0];
    groups.set(key, (groups.get(key) ?? new Map<string, Set<string>>()).set(category, codesInCategory));
  }

  return [...groups.entries()]
    .sort(([a], [b]) => compareIcd10Codes(a, b))
    .map(([key, categories]) => {
      const items = [...categories.entries()]
        .sort(([a], [b]) => compareIcd10Codes(a, b))
        .map(([category, codesInCategory]) => ({ category, codes: [...codesInCategory].sort(compareIcd10Codes) }));

      return { key, count: items.reduce((sum, item) => sum + item.codes.length, 0), categories: items };
    });
}

/**
 * The given codes that answer a search: those the dictionary search returned, plus those starting with the term
 * (so a code search never depends on the dictionary's page size). Sorted numerically.
 */
export function matchIcd10Codes(codes: string[], term: string, found: Record<string, string>): string[] {
  const prefix = term.trim().toUpperCase();
  const distinct = new Set(codes.map(code => code.trim().toUpperCase()));

  return [...distinct].filter(code => code in found || code.startsWith(prefix)).sort(compareIcd10Codes);
}

/** Whether a code's category falls in a block's `start-end` range (`O98.4` is in `O94-O99`). */
export function isIcd10CodeInBlock(code: string, block: string): boolean {
  const [start, end = start] = block.toUpperCase().split('-');
  const category = icd10Category(code.trim().toUpperCase());
  return start <= category && category <= end;
}

/** Whether a code sits under a dictionary entry: within a block's range, or under a category or diagnosis code. */
export function isIcd10CodeUnder(code: string, entry: Pick<Icd10DictionaryEntry, 'code' | 'level'>): boolean {
  const normalized = code.trim().toUpperCase();
  return entry.level <= ICD10_BLOCK_LEVEL ? isIcd10CodeInBlock(normalized, entry.code) : normalized.startsWith(entry.code.toUpperCase());
}

/**
 * Letter + first digit prefixes spanning a block range (`O94-O99` → `O9`; `C00-C14` → `C0`, `C1`): the dictionary
 * search terms that find every category of the block, which is then cut back to the range.
 */
export function icd10BlockPrefixes(block: string): string[] {
  const [start, end = start] = block.toUpperCase().split('-');
  const prefixes: string[] = [];

  for (let letter = start.charCodeAt(0); letter <= end.charCodeAt(0); letter++) {
    const from = letter === start.charCodeAt(0) ? Number(start[1]) : 0;
    const to = letter === end.charCodeAt(0) ? Number(end[1]) : 9;
    for (let digit = from; digit <= to; digit++) prefixes.push(`${String.fromCharCode(letter)}${digit}`);
  }

  return prefixes;
}

/**
 * The lowest-level diagnoses among dictionary entries — what an option stores: categories and below
 * (blocks and classes are never diagnoses) with no deeper entry under their code. Deduped, sorted numerically.
 */
export function icd10LeafEntries(entries: Icd10DictionaryEntry[]): Icd10DictionaryEntry[] {
  const byCode = new Map<string, Icd10DictionaryEntry>();
  for (const entry of entries) {
    if (entry.level >= ICD10_CATEGORY_LEVEL) byCode.set(entry.code.trim().toUpperCase(), { ...entry, code: entry.code.trim().toUpperCase() });
  }

  const diagnoses = [...byCode.values()];
  return diagnoses.filter(entry => !diagnoses.some(other => other.level > entry.level && other.code.startsWith(entry.code))).sort((a, b) => compareIcd10Codes(a.code, b.code));
}

/**
 * The dictionary entry a tree node stands for — a block (`O94-O99`), a category (`O98`) or a code (`O98.4`) —
 * or null for a letter group, which stands for no single entry (see `groupIcd10Codes`).
 */
export function icd10NodeEntry(key: string): Icd10DictionaryEntry | null {
  if (key.length < ICD10_CATEGORY_LEVEL) return null;
  if (key.includes('-')) return { code: key, level: ICD10_BLOCK_LEVEL };
  return { code: key, level: key.includes('.') ? ICD10_CATEGORY_LEVEL + 1 : ICD10_CATEGORY_LEVEL };
}

function toBlockRanges(blockCodes: string[]): Icd10BlockRange[] {
  return blockCodes.map(code => {
    const [start, end = start] = code.toUpperCase().split('-');
    return { code, start, end };
  });
}
